import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { ButtonGroup, ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { useClick2PayProfile } from '@/react-query/hooks/query/use-click2pay';
import { useClick2PayIsAllowed } from '@/react-query/hooks/useClick2PayIsAllowed';

import useText from '@hooks/use-text';
import { getClick2PayUrl } from '@pages/click2pay/functions';
import click2PayRoutes from '@pages/click2pay/routes';

import './Click2PayTermsAndConditions.scss';

const Click2PayTermsAndConditions = () => {
    const { click2PayGetGoing, click2PayPrevious, click2PayCancel } = useText();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const { infoReview, admin, onboarding } = click2PayRoutes;
    const profile = useClick2PayProfile();
    const click2PayIsAllowed = useClick2PayIsAllowed();

    useEffect(() => {
        if (!click2PayIsAllowed) {
            navigate('/');
        }
    }, [click2PayIsAllowed, navigate]);

    const next = () => {
        if (profile.data && profile.data?.customerProfile) {
            return navigate(getClick2PayUrl(queryParams, admin));
        }
        return navigate(getClick2PayUrl(queryParams, infoReview));
    };
    const previous = () => {
        return navigate(getClick2PayUrl(queryParams, onboarding));
    };

    return (
        <InfoCard skipBackground className="click2pay-terms-card">
            <RichIcon type={RichIconTypes.DIGITALTKORT_ABONNEMENT} />
            <h1 className="click2pay-terms-header">Vilkår og betingelser</h1>

            <section>
                <h3>Enklere netthandel med Click to Pay fra Visa</h3>
                <p>
                    Nå kan du handle på nett uten å taste inn kortinformasjon hver gang. Med{' '}
                    <strong>Click to Pay</strong> lagrer du kortet ditt én gang – så slipper du å skrive inn kortnummer,
                    utløpsdato og sikkerhetskode neste gang du handler hos nettbutikker som støtter løsningen.
                </p>
            </section>
            <section>
                <h3>Slik fungerer det:</h3>
                <ul>
                    <li>Kortinformasjonen og kontaktopplysningene dine lagres trygt hos Visa.</li>
                    <li>Når du handler hos en nettbutikk merket med Click to Pay, fylles alt inn automatisk.</li>
                    <li>Du bekrefter kjøpet enkelt – uten ekstra steg.</li>
                </ul>
            </section>

            <section>
                <h3>Trygt og kontrollert</h3>
                <p>
                    Banken, Visa og nettbutikken deler kun det som er nødvendig for å gjennomføre kjøpet: navn, e-post,
                    telefonnummer, adresse og kortdetaljer.
                </p>
            </section>
            <section>
                <h3>Du har kontrollen</h3>
                <p>Du kan når som helst skru av Click to Pay i mobilbanken, nettbanken – eller ta kontakt med oss.</p>
            </section>
            <ButtonGroup
                primaryAction={
                    <StandardButton onClick={next} disabled={profile.isPending}>
                        {click2PayGetGoing}
                    </StandardButton>
                }
                secondaryAction={
                    <StandardButton
                        buttonType={ButtonTypes.STANDARD_NEGATIVE}
                        onClick={previous}
                        disabled={profile.isPending}
                    >
                        {click2PayPrevious}
                    </StandardButton>
                }
                tertiaryAction={<LinkButton onClick={() => navigate('/')}>{click2PayCancel}</LinkButton>}
            />
        </InfoCard>
    );
};

export default Click2PayTermsAndConditions;
